import { useEffect, useState } from "react";
import api, { API, apiError } from "../../lib/api";
import { Card, btnSecondary } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { FileText, Download, Eye, Mail, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ST_COLOR = { sent: "text-emerald-600", failed: "text-rose-600", sending: "text-amber-600", queued: "text-amber-600", not_sent: "text-slate-500" };
const ST_LABEL = { sent: "Sent", failed: "Failed", sending: "Sending", queued: "Queued", not_sent: "Not Sent" };

function StatusRows({ n, fallbackRecipient, testId, extra }) {
  const st = n?.status || "not_sent";
  return (
    <div className="text-sm space-y-1">
      <div className="flex justify-between gap-3"><span className="text-slate-500">Recipient</span><span className="font-mono text-slate-700 text-xs truncate">{n?.recipient || fallbackRecipient || "—"}</span></div>
      <div className="flex justify-between"><span className="text-slate-500">Status</span><span className={`font-semibold ${ST_COLOR[st]}`} data-testid={testId}>{ST_LABEL[st] || st}</span></div>
      {n?.sent_at && <div className="flex justify-between"><span className="text-slate-500">Sent At</span><span className="text-slate-700">{new Date(n.sent_at).toLocaleString()}</span></div>}
      {extra}
      {n?.error && <div className="text-xs text-rose-600 pt-1 break-words">{n.error}</div>}
    </div>
  );
}

export function PaymentNotifications({ reg, refreshKey }) {
  const rid = reg?.id;
  const completed = reg?.payment_status === "verified";
  const [inv, setInv] = useState(null);
  const [wa, setWa] = useState(null);
  const [em, setEm] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    if (!rid) return;
    api.get(`/whatsapp/notification/${rid}`).then((r) => setWa(r.data)).catch(() => setWa(null));
    api.get(`/email/notification/${rid}`).then((r) => setEm(r.data)).catch(() => setEm(null));
    if (completed) api.get(`/public/invoice/${rid}`).then((r) => setInv(r.data)).catch(() => setInv(null));
  };
  useEffect(() => { loadAll(); const t = setTimeout(loadAll, 4000); return () => clearTimeout(t); }, [rid, completed, refreshKey]); // eslint-disable-line

  if (!rid || !completed) return null;
  const pdfUrl = `${API}/public/invoice/${rid}/pdf`;

  const send = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/${kind === "email" ? "email" : "whatsapp"}/resend/${rid}`);
      const n = data.notification;
      (kind === "email" ? setEm : setWa)((p) => ({ ...(p || {}), notification: n }));
      if (n?.status === "sent") toast.success(kind === "email" ? "Invoice emailed to the parent" : "WhatsApp message sent");
      else toast.error(n?.error || `${kind === "email" ? "Email" : "WhatsApp"} send failed — see status`);
      setConfirm(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <>
      <Card className="p-6 space-y-3" data-testid="invoice-card">
        <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Invoice / Receipt</h3>
        <div className="text-sm space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Invoice No</span><span className="font-mono text-slate-700" data-testid="invoice-no">{inv?.invoice_no || "—"}</span></div>
          {inv?.invoice_date && <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="text-slate-700">{inv.invoice_date}</span></div>}
          {inv?.total != null && <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="text-slate-700 font-semibold">{"\u20b9" + Number(inv.total).toLocaleString("en-IN")}</span></div>}
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <a href={pdfUrl.replace("/pdf", "/pdf?inline=1")} target="_blank" rel="noreferrer" className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm" data-testid="invoice-view-btn"><Eye className="w-4 h-4" /> View</a>
          <a href={pdfUrl} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm" data-testid="invoice-download-btn"><Download className="w-4 h-4" /> Download</a>
        </div>
        <div className="pt-2 border-t border-slate-100 space-y-2">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Send Manually</div>
          <button className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-60" onClick={() => setConfirm("email")} disabled={busy || !em?.configured} data-testid="invoice-send-email-btn"><Mail className="w-4 h-4" /> Send Invoice via Email</button>
          <button className="w-full bg-[#25d366]/10 hover:bg-[#25d366]/20 text-emerald-800 border border-emerald-200 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-60" onClick={() => setConfirm("whatsapp")} disabled={busy || !wa?.enabled} data-testid="invoice-send-wa-btn"><MessageCircle className="w-4 h-4" /> Send via WhatsApp</button>
          {wa && !wa.enabled && <p className="text-[11px] text-slate-400">WhatsApp messaging is not enabled for this page.</p>}
        </div>
      </Card>

      {wa?.enabled && (
        <Card className="p-6 space-y-3" data-testid="wa-notification-card">
          <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#25d366]" /> WhatsApp Notification</h3>
          <StatusRows n={wa.notification} fallbackRecipient={reg.mobile} testId="wa-status" />
        </Card>
      )}
      {em?.enabled && (
        <Card className="p-6 space-y-3" data-testid="email-notification-card">
          <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Email Confirmation</h3>
          <StatusRows n={em.notification} fallbackRecipient={reg.email} testId="email-status"
            extra={em.notification?.invoice_no && <div className="flex justify-between"><span className="text-slate-500">Invoice</span><span className="text-slate-700">{em.notification.invoice_no} (PDF attached)</span></div>} />
        </Card>
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="send-invoice-dialog">
          <DialogHeader><DialogTitle>{confirm === "email" ? "Send Invoice via Email?" : "Send WhatsApp Message?"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{confirm === "email" ? "The payment confirmation email with the PDF invoice attached will be sent to the parent's email address." : "The registration completion message (with the invoice link) will be sent to the parent's WhatsApp number."}</p>
          <DialogFooter>
            <button className={btnSecondary} onClick={() => setConfirm(null)}>Cancel</button>
            <button className={`${confirm === "email" ? "bg-blue-600 hover:bg-blue-700" : "bg-[#25d366] hover:brightness-95"} text-white font-semibold py-2.5 px-4 rounded-xl flex items-center gap-2 disabled:opacity-60`} onClick={() => send(confirm)} disabled={busy} data-testid="confirm-send-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Now</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
